import Observer from "../../classes/Observer.js";
import Dialog from "../../classes/Dialog.js";
import TokenStore from "../../classes/TokenStore.js";
import {
    empty,
    lookupOne,
    lookupOneCached,
    replaceContentsMany
} from "../../utils/elements.js";
import {
    shuffle
} from "../../utils/arrays.js";

const gameObserver = Observer.create("game");

// Store the current character assignment state
let secretAssignmentState = {
    availableCharacters: [],
    selectedCharacter: null, // The character selected from overlay
    isEnabled: false,
    isOverlayMode: false, // Whether we're showing overlays or numbers
    characterMapping: {} // Maps character IDs to actual character objects
};

// Expose to global scope for other modules to access
window.secretAssignmentState = secretAssignmentState;

// Initialize the secret assignment functionality
function initializeSecretAssignment() {
    const toggleButton = lookupOne("#secret-assignment-toggle");
    
    // Toggle secret assignment on/off
    if (toggleButton) {
        toggleButton.addEventListener("click", () => {
            secretAssignmentState.isEnabled = !secretAssignmentState.isEnabled;
            toggleButton.classList.toggle("button--active", secretAssignmentState.isEnabled);
            toggleButton.textContent = secretAssignmentState.isEnabled 
                ? "Fullscreen Mode: ON" 
                : "Fullscreen Mode: OFF";
            
            console.log("Secret assignment toggled:", secretAssignmentState.isEnabled);
            
            // If turning off, clear any current state
            if (!secretAssignmentState.isEnabled) {
                clearSecretAssignmentState();
            }
            
            // Update the character choice display if it's currently shown
            updateCharacterChoiceDisplay();
        });
    }
}

// Clear the secret assignment state
function clearSecretAssignmentState() {
    secretAssignmentState.selectedCharacter = null;
    secretAssignmentState.isOverlayMode = false;
    removeAllOverlays();
}

// Remove all character overlays
function removeAllOverlays() {
    const wrapper = lookupOneCached("#character-choice-wrapper");
    if (wrapper) {
        const overlays = wrapper.querySelectorAll(".character-overlay");
        overlays.forEach(overlay => overlay.remove());
    }
}

// Update the character choice display with overlays or numbers
function updateCharacterChoiceDisplay() {
    const wrapper = lookupOneCached("#character-choice-wrapper");
    console.log("updateCharacterChoiceDisplay called", {
        wrapper: !!wrapper,
        enabled: secretAssignmentState.isEnabled,
        availableCharacters: secretAssignmentState.availableCharacters.length,
        isOverlayMode: secretAssignmentState.isOverlayMode
    });
    
    if (!wrapper) {
        console.log("No wrapper found");
        return;
    }
    
    if (!secretAssignmentState.isEnabled) {
        console.log("Secret assignment not enabled");
        return;
    }
    
    const buttons = wrapper.querySelectorAll(".character-choice");
    console.log("Found buttons:", buttons.length);
    
    if (secretAssignmentState.isOverlayMode && secretAssignmentState.availableCharacters.length > 0) {
        console.log("Showing character overlays");
        // Show character overlays only on available (non-disabled) buttons
        let characterIndex = 0;
        buttons.forEach((button, index) => {
            // Only add overlay if button is not disabled and we have characters left
            if (!button.disabled && characterIndex < secretAssignmentState.availableCharacters.length) {
                const character = secretAssignmentState.availableCharacters[characterIndex];
                console.log("Adding overlay for character:", character.getName(), "on button", index);
                addCharacterOverlay(button, character);
                characterIndex++;
            }
        });
    } else {
        console.log("Showing numbers (removing overlays)");
        // Show numbers (remove overlays)
        removeAllOverlays();
    }
}

// Add character overlay to a numbered button
function addCharacterOverlay(button, character) {
    console.log("Adding overlay for character:", character.getName());
    
    // Remove existing overlay if any
    const existingOverlay = button.querySelector(".character-overlay");
    if (existingOverlay) {
        existingOverlay.remove();
    }
    
    // Create overlay
    const overlay = document.createElement("div");
    overlay.className = "character-overlay";
    
    // Add character image
    const img = document.createElement("img");
    img.src = character.getImage();
    img.alt = character.getName();
    img.className = "character__icon";
    overlay.appendChild(img);
    
    // Add tooltip with character name
    overlay.title = `Click to secretly select ${character.getName()}`;
    
    // Add click handler
    overlay.addEventListener("click", (e) => {
        e.stopPropagation();
        selectCharacterFromOverlay(character);
    });
    
    // Make button relative positioned if not already
    if (getComputedStyle(button).position === "static") {
        button.style.position = "relative";
    }
    
    button.appendChild(overlay);
    console.log("Overlay added successfully");
}

// Select a character from the overlay (step 2)
function selectCharacterFromOverlay(character) {
    secretAssignmentState.selectedCharacter = character;
    secretAssignmentState.isOverlayMode = false;
    
    console.log("Character selected from overlay:", character.getName());
    
    // Show visual feedback
    const toggleButton = lookupOne("#secret-assignment-toggle");
    if (toggleButton) {
        const originalText = toggleButton.textContent;
        toggleButton.textContent = `Selected: ${character.getName()}`;
        setTimeout(() => {
            if (secretAssignmentState.isEnabled) {
                toggleButton.textContent = "Fullscreen Mode: ON";
            }
        }, 2000);
    }
    
    // Hide all overlays to reveal numbers
    updateCharacterChoiceDisplay();
}

// Handle character draw event to set up secret assignment
gameObserver.on("character-draw", ({ detail }) => {
    console.log("Character draw event received", {
        characters: detail.characters.length,
        isShowAll: detail.isShowAll
    });
    
    if (detail.isShowAll) {
        console.log("Skipping show all mode");
        return;
    }
    
    // Store available characters for secret assignment
    secretAssignmentState.availableCharacters = [...detail.characters];
    secretAssignmentState.selectedCharacter = null;
    secretAssignmentState.isOverlayMode = true; // Start in overlay mode
    
    // Create mapping from character IDs to character objects
    secretAssignmentState.characterMapping = {};
    detail.characters.forEach(character => {
        secretAssignmentState.characterMapping[character.getId()] = character;
    });
    
    console.log("Secret assignment state updated", {
        availableCharacters: secretAssignmentState.availableCharacters.length,
        isEnabled: secretAssignmentState.isEnabled,
        isOverlayMode: secretAssignmentState.isOverlayMode
    });
    
    // Update display after a short delay to ensure DOM is ready
    setTimeout(() => {
        console.log("Calling updateCharacterChoiceDisplay after delay");
        updateCharacterChoiceDisplay();
    }, 100);
});

// Override the character choice click handler to use secret assignment
gameObserver.on("character-choice-click", ({ detail }) => {
    console.log("Character choice click event received", {
        enabled: secretAssignmentState.isEnabled,
        selectedCharacter: secretAssignmentState.selectedCharacter?.getName(),
        isOverlayMode: secretAssignmentState.isOverlayMode
    });
    
    // Only process if secret assignment is enabled and we have a selected character
    if (!secretAssignmentState.isEnabled || !secretAssignmentState.selectedCharacter) {
        console.log("Secret assignment not active, allowing normal processing");
        return;
    }
    
    // Mark this event as processed to prevent fallback handler
    detail.processed = true;
    
    console.log("Player clicked number, giving them:", secretAssignmentState.selectedCharacter.getName());
    
    // Use the selected character instead of the clicked one
    const selectedCharacter = secretAssignmentState.selectedCharacter;
    
    // Trigger character drawn event with the selected character
    console.log("Triggering character-drawn event with:", selectedCharacter.getName());
    gameObserver.trigger("character-drawn", {
        element: detail.element,
        character: selectedCharacter.clone()
    });
    
    // Remove the assigned character from available pool
    const characterIndex = secretAssignmentState.availableCharacters.findIndex(
        char => char.getId() === selectedCharacter.getId()
    );
    if (characterIndex !== -1) {
        secretAssignmentState.availableCharacters.splice(characterIndex, 1);
    }
    
    // Also remove from character mapping
    delete secretAssignmentState.characterMapping[selectedCharacter.getId()];
    
    // Reset for next player
    secretAssignmentState.selectedCharacter = null;
    secretAssignmentState.isOverlayMode = true;
    
    // Update display for next player
    setTimeout(() => {
        updateCharacterChoiceDisplay();
    }, 100);
});

// Initialize when the module loads
initializeSecretAssignment();
